import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { Eye } from 'lucide-react';
import { ApiError } from '../../../lib/api';
import { operatorConsole, type PackValidation } from '../../../lib/api/operatorConsole';
import { Button } from '../../../components/ui/Button';
import { Pill } from '../../../components/ui/Pill';
import { PackHealthPanel } from '../items/PackHealthPanel';
import { PreviewModal } from '../PreviewModal';
import { formatMoney, formatPercent, isLive, isStaged, packStatusLabel } from '../packBuilderFormat';
import type { PackTabProps } from './packTabProps';

const SECTION = 'space-y-3 rounded-xl border border-[var(--co-border)] bg-[var(--co-surface)] p-4';

function footnote(live: boolean, restocking: boolean, staged: boolean): string {
  if (!live && !staged) {
    return 'Support has disabled this pack. It is read-only here: it cannot be published, edited or deleted — contact support to bring it back.';
  }
  if (restocking) {
    return 'While the pack is restocking the stock machine owns its availability: it comes back on sale by itself once the stock is refilled. Price, RTP and buckets stay locked and it cannot be unpublished or deleted from here.';
  }
  if (live) {
    return 'Unpublishing hides the pack from players; cards and settings stay. Price, RTP and buckets unlock once it is unpublished.';
  }
  // A staged pack says nothing: the Publish button speaks for itself, and the
  // states above are the ones that need explaining.
  return '';
}

export function PublishTab({ pack, currency, onPackChange, notify }: PackTabProps) {
  const navigate = useNavigate();
  const packCurrency = pack.currency ?? currency;
  // enabled and restocking both count as published (CONTRACT §4.8). A
  // restocking pack is driven by the stock machine: no Publish, no Unpublish,
  // no Delete — only the read-only summary, the report and the preview.
  const live = isLive(pack);
  const restocking = pack.availability === 'restocking';
  // Only `staged` is the operator's to publish or delete: `disabled` (support)
  // and `default` are read-only, and the backend answers 409 for them.
  const staged = isStaged(pack);
  const hint = footnote(live, restocking, staged);
  const [validation, setValidation] = useState<PackValidation | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      setValidation(await operatorConsole.packBuilder.validate(pack.id, {}));
    } catch (err) {
      notify('error', err instanceof Error ? err.message : 'Validation failed');
    } finally {
      setLoading(false);
    }
  }, [pack.id, notify]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // The iframe lists packs by category, so a pack without one would be live
  // and invisible at the same time.
  const missingCategory = pack.pool_category_ids.length === 0;
  const canPublish = staged && !busy && !loading && validation?.ok === true && !missingCategory;

  async function handlePublish() {
    setBusy(true);
    setFailure(null);
    try {
      const updated = await operatorConsole.packBuilder.publish(pack.id);
      onPackChange(updated);
      notify('success', 'Pack published');
    } catch (err) {
      if (err instanceof ApiError && err.code === 'VALIDATION_FAILED') {
        // The 422 body carries the report, but apiRequest keeps only the
        // message — re-run the validator for the details.
        setFailure(err.message);
        await refresh();
      } else {
        // NO_POOL_CATEGORY (422), AVAILABILITY_TRANSITION_FORBIDDEN (409),
        // PUBLISH_FAILED_AFTER_ACTUALIZE (422): the message is the whole
        // story (CONTRACT §4.20) — a toast, no report refresh.
        notify('error', err instanceof Error ? err.message : 'Could not publish');
      }
    } finally {
      setBusy(false);
    }
  }

  async function handleUnpublish() {
    setBusy(true);
    try {
      const updated = await operatorConsole.packBuilder.unpublish(pack.id);
      onPackChange(updated);
      notify('success', 'Pack unpublished');
    } catch (err) {
      notify('error', err instanceof Error ? err.message : 'Could not unpublish');
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete() {
    if (
      !window.confirm(
        `Delete "${pack.title}"? The cards stay in the inventory; only the pack goes away.`,
      )
    ) {
      return;
    }
    setBusy(true);
    try {
      await operatorConsole.packBuilder.deletePack(pack.id);
      navigate('/my-packs');
    } catch (err) {
      // PACK_HAS_ORDERS, PACK_PUBLISHED and PACK_SUPPORT_DISABLED (all 409)
      // arrive here with the backend message. The last one is unreachable from
      // this button (Delete is hidden unless the pack is staged) — the backend
      // refuses it anyway, for hand-made requests.
      notify('error', err instanceof Error ? err.message : 'Could not delete the pack');
      setBusy(false);
    }
  }

  const status = packStatusLabel(pack);

  return (
    <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_360px]">
      <section className={SECTION}>
        <div className="flex items-center gap-2">
          <h2 className="text-[13px] font-semibold text-[var(--co-text)]">Status</h2>
          <Pill tone={status.tone}>{status.label}</Pill>
        </div>

        <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-[12px]">
          <dt className="text-[var(--co-muted)]">Price</dt>
          <dd className="font-mono">{formatMoney(pack.price, packCurrency)}</dd>
          <dt className="text-[var(--co-muted)]">RTP</dt>
          <dd className="font-mono">{formatPercent(pack.stated_rtp)}</dd>
          <dt className="text-[var(--co-muted)]">Cards in pack</dt>
          <dd className="font-mono">{pack.items_count}</dd>
          <dt className="text-[var(--co-muted)]">Categories</dt>
          <dd className="font-mono">{pack.pool_category_ids.length}</dd>
        </dl>

        {restocking && (
          <p className="rounded-lg border border-[var(--co-border)] bg-[var(--co-bg)] px-3 py-2 text-[12px] text-[var(--co-text)]">
            Restocking — managed automatically
          </p>
        )}
        {!live && !staged && (
          <p className="rounded-lg border border-[var(--co-border)] bg-[var(--co-bg)] px-3 py-2 text-[12px] text-[var(--co-text)]">
            Disabled by support — read-only
          </p>
        )}
        {missingCategory && staged && (
          <p className="text-[12px] text-[var(--co-warning)]">
            Add at least one category on the Details tab before publishing.
          </p>
        )}
        {failure && (
          <p role="alert" className="text-[12px] text-[var(--co-error)]">
            {failure}
          </p>
        )}

        <div className="flex flex-wrap gap-2">
          {staged && (
            <Button onClick={() => void handlePublish()} disabled={!canPublish}>
              Publish
            </Button>
          )}
          {live && !restocking && (
            <Button variant="ghost" onClick={() => void handleUnpublish()} disabled={busy}>
              Unpublish
            </Button>
          )}
          {staged && (
            <Button
              variant="ghost"
              onClick={() => void handleDelete()}
              disabled={busy}
              className="text-[var(--co-error)]"
            >
              Delete pack
            </Button>
          )}
          <Button
            variant="ghost"
            onClick={() => setPreviewOpen(true)}
            disabled={busy}
            className="gap-2"
          >
            <Eye size={14} aria-hidden="true" />
            Preview
          </Button>
        </div>

        {hint ? <p className="text-[11px] text-[var(--co-muted)]">{hint}</p> : null}
      </section>

      <PackHealthPanel
        validation={validation}
        loading={loading}
        currency={packCurrency}
        title="Validation"
      />

      <PreviewModal pack={pack} open={previewOpen} onClose={() => setPreviewOpen(false)} />
    </div>
  );
}
